/**
 * API Client using sentry-fetch for making HTTP requests
 * 
 * This client provides a centralized way to make API calls with:
 * - Error handling
 * - Retry logic
 * - Authentication via Midway Interceptor
 * - Monitoring capabilities
 */

import sentryFetch from "@amzn/sentry-fetch";

// Configuration constants

const API_ENDPOINTS = {
  test: 'https://api.beta.prism.rme.amazon.dev', // local development
  dev: 'https://api.prism.dmmatall.people.amazon.dev',
  beta: 'https://api.beta.prism.rme.amazon.dev',
  prod: 'https://api.prism.rme.amazon.dev',
};


// Error types
export enum ApiErrorType {
  NETWORK = 'NETWORK',
  TIMEOUT = 'TIMEOUT',
  SERVER = 'SERVER',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION = 'VALIDATION',
  UNKNOWN = 'UNKNOWN',
}

// API Error class
export class ApiError extends Error {
  public readonly type: ApiErrorType;
  public readonly statusCode?: number;
  public readonly data?: any;

  constructor(message: string, type: ApiErrorType, statusCode?: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.statusCode = statusCode;
    this.data = data;
  }
}

// Request options interface
export interface ApiRequestOptions {
  timeout?: number;
  retries?: number;
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * Maps HTTP status codes to ApiErrorType
 */
const mapStatusToErrorType = (status: number): ApiErrorType => {
  if (status === 401) return ApiErrorType.UNAUTHORIZED;
  if (status === 403) return ApiErrorType.FORBIDDEN;
  if (status === 404) return ApiErrorType.NOT_FOUND;
  if (status >= 400 && status < 500) return ApiErrorType.VALIDATION;
  if (status >= 500) return ApiErrorType.SERVER;
  return ApiErrorType.UNKNOWN;
};

const getStage = (): string => {
  // This stage is replaced during building
  // https://katal.amazon.dev/build-and-deploy/tutorials/deployment-manifest-templating/
  return "dev";
}

/**
 * Builds the URL with query parameters
 */
const buildUrl = (path: string, params?: Record<string, string | number | boolean | undefined>): string => {
  // @ts-ignore
  const endpoint = API_ENDPOINTS[getStage()];
  const url = new URL(`${endpoint}${path}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
};

/**
 * Handles API response and errors
 */
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      errorData = { message: 'Unknown error' };
    }

    const errorType = mapStatusToErrorType(response.status);
    throw new ApiError(
      errorData.message || `Request failed with status ${response.status}`,
      errorType,
      response.status,
      errorData
    );
  }

  // For 204 No Content responses
  if (response.status === 204) {
    return {} as T;
  }

  try {
    return await response.json() as T;
  } catch (error) {
    throw new ApiError(
      'Failed to parse response as JSON',
      ApiErrorType.UNKNOWN,
      response.status
    );
  }
};

/**
 * API Client with methods for making HTTP requests
 */
export const apiClient = {
  /**
   * Makes a GET request
   * @param path - API endpoint path
   * @param options - Optional request configuration
   * @returns Promise resolving to response data
   */
  async get<T>(path: string, options?: ApiRequestOptions): Promise<T> {
    try {
      let response;
      // if (getStage() === "test") {
      //   // for local development the midway auth is disabled
      //   response = await fetch(buildUrl(path, options?.params), {
      //     method: 'GET',
      //     mode: 'cors',
      //     redirect: 'follow',
      //     headers: {
      //       'Content-Type': 'application/json',
      //       ...options?.headers,
      //     },
      //   });  
      // } else {
        response = await sentryFetch(buildUrl(path, options?.params), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
          },
          
        });
      // }

      return handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle network errors from sentry-fetch
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timed out', ApiErrorType.TIMEOUT);
        }
        throw new ApiError(error.message, ApiErrorType.NETWORK);
      }

      throw new ApiError('Unknown error occurred', ApiErrorType.UNKNOWN);
    }
  },

  /**
   * Makes a POST request
   * @param path - API endpoint path
   * @param data - Request body data
   * @param options - Optional request configuration
   * @returns Promise resolving to response data
   */
  async post<T>(path: string, data: any, options?: ApiRequestOptions): Promise<T> {
    try {
      const response = await sentryFetch(buildUrl(path, options?.params), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        body: JSON.stringify(data),
      });

      return handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timed out', ApiErrorType.TIMEOUT);
        }
        throw new ApiError(error.message, ApiErrorType.NETWORK);
      }

      throw new ApiError('Unknown error occurred', ApiErrorType.UNKNOWN);
    }
  },

  /**
   * Makes a PUT request
   * @param path - API endpoint path
   * @param data - Request body data
   * @param options - Optional request configuration
   * @returns Promise resolving to response data
   */
  async put<T>(path: string, data: any, options?: ApiRequestOptions): Promise<T> {
    try {
      const response = await sentryFetch(buildUrl(path, options?.params), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        body: JSON.stringify(data),
      });

      return handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timed out', ApiErrorType.TIMEOUT);
        }
        throw new ApiError(error.message, ApiErrorType.NETWORK);
      }

      throw new ApiError('Unknown error occurred', ApiErrorType.UNKNOWN);
    }
  },

  /**
   * Makes a DELETE request
   * @param path - API endpoint path
   * @param options - Optional request configuration
   * @returns Promise resolving to response data
   */
  async delete<T>(path: string, options?: ApiRequestOptions): Promise<T> {
    try {
      const response = await sentryFetch(buildUrl(path, options?.params), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
      });

      return handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timed out', ApiErrorType.TIMEOUT);
        }
        throw new ApiError(error.message, ApiErrorType.NETWORK);
      }

      throw new ApiError('Unknown error occurred', ApiErrorType.UNKNOWN);
    }
  },
};